import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { normalizeVcpNumber } from '../utils/vcpFormat';

// The "Edit VCP details" modal opened from a VcpRow's VCP-pattern cell.
// Shared by VCP.js (the source page) and TickerSearch.js so editing a VCP
// pattern behaves identically from either place. Owns only its own form
// state; the actual save (API call + list update) is delegated to the
// caller via `onSave`, since Vcp.js and TickerSearch.js keep their matched
// items in different places.
const VcpEditorModal = ({ show, item, onClose, onSave }) => {
  const [form, setForm] = useState({ lengthWeeks: '', times: '', highPct: '', lowPct: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && item) {
      setForm({
        lengthWeeks: item.vcp_length_weeks ?? '',
        times: item.vcp_times ?? '',
        highPct: item.vcp_high_pct ?? '',
        lowPct: item.vcp_low_pct ?? ''
      });
      setError('');
    }
  }, [show, item]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!item || saving) return;
    setSaving(true);
    setError('');
    const payload = {
      vcp_length_weeks: normalizeVcpNumber(form.lengthWeeks),
      vcp_times: normalizeVcpNumber(form.times),
      vcp_high_pct: normalizeVcpNumber(form.highPct),
      vcp_low_pct: normalizeVcpNumber(form.lowPct)
    };
    try {
      await onSave(item.id, payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update VCP details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>VCP Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="text-danger mb-2">{error}</div>}
        <Form>
          <Form.Group className="mb-3" controlId="vcp-length">
            <Form.Label>Length (Weeks)</Form.Label>
            <Form.Control
              type="number"
              value={form.lengthWeeks}
              onChange={(e) => setForm(prev => ({ ...prev, lengthWeeks: e.target.value }))}
              min="0"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="vcp-times">
            <Form.Label>Times (Up & Down)</Form.Label>
            <Form.Control
              type="number"
              value={form.times}
              onChange={(e) => setForm(prev => ({ ...prev, times: e.target.value }))}
              min="0"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="vcp-high">
            <Form.Label>High %</Form.Label>
            <Form.Control
              type="number"
              value={form.highPct}
              onChange={(e) => setForm(prev => ({ ...prev, highPct: e.target.value }))}
              min="0"
              step="0.1"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="vcp-low">
            <Form.Label>Low %</Form.Label>
            <Form.Control
              type="number"
              value={form.lowPct}
              onChange={(e) => setForm(prev => ({ ...prev, lowPct: e.target.value }))}
              min="0"
              step="0.1"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VcpEditorModal;
